import { useCallback, useEffect, useState } from 'react'

const useOverflow = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  direction: 'horizontal' | 'vertical' = 'horizontal'
) => {
  const [hasOverflow, setHasOverflow] = useState(false)
  const [scrollStarted, setScrollStarted] = useState(false)
  const [scrollEnded, setScrollEnded] = useState(false)

  const checkOverflow = useCallback(() => {
    if (!ref.current) return
    if (direction === 'horizontal') {
      setHasOverflow(ref.current.scrollWidth > ref.current.clientWidth)
    } else {
      setHasOverflow(ref.current.scrollHeight > ref.current.clientHeight)
    }
  }, [ref, direction])

  useEffect(() => {
    checkOverflow()

    window.addEventListener('resize', checkOverflow)

    const observer = new ResizeObserver(checkOverflow)
    if (ref.current) observer.observe(ref.current)

    return () => {
      window.removeEventListener('resize', checkOverflow)
      observer.disconnect()
    }
  }, [checkOverflow, ref])

  useEffect(() => {
    if (!ref.current) return

    const el = ref.current
    const tolerance = 1

    const handleScroll = () => {
      if (direction === 'horizontal') {
        setScrollStarted(el.scrollLeft > 0)
        setScrollEnded(
          hasOverflow &&
          el.scrollLeft + el.clientWidth >= el.scrollWidth - tolerance
        )
      } else {
        setScrollStarted(el.scrollTop > 0)
        setScrollEnded(
          hasOverflow &&
          el.scrollTop + el.clientHeight >= el.scrollHeight - tolerance
        )
      }
    }

    el.addEventListener('scroll', handleScroll)

    handleScroll()

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener('scroll', handleScroll)
    }
  }, [ref, direction, hasOverflow])

  return { hasOverflow, scrollStarted, scrollEnded }
}

export default useOverflow
